import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import type {DownloadColorProfileOptionType} from '@Components/download-panel/components/download-color-profile-option/download-color-profile-option.types';
import type {AJAXResponsePurchaseProduct} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import type {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {transparencyStates} from '@PosterWhiteboard/page/background/image-background.class';
import {getPosterWidthAndHeight, getUnitsFromPosterTypeObject, getWidthAndHeightInPixels, isExpensiveVideoPoster, isPosterVideo, processThumbnailsFromArrayToHashmap, PosterTypeUnits} from '@Libraries/poster-object.library';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import {readCookie, setCookie} from '@Utils/cookie.util';
import {isUserPAYG, isUserPremium} from '@Libraries/user.library';
import {onConvertTrialFailCallback} from '@Panels/poster-publishing-options-panel/publishing-process-design-library';
import {isIOSApp} from '@Utils/browser.util';
import type {IAPProduct} from '@Panels/change-plan-modal/change-plan-modal.types';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.types';
import {noop} from '@Utils/general.util';

const WEB_DOWNLOAD_PRODUCT_ID = 63;
export const CHEAP_PRODUCT_ID = 85;
export const EXPENSIVE_PRODUCT_ID = 86;
export const MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT = 9;
export const DEFAULT_DOWNLOAD_CURRENCY = 'USD';
export const MULTIPAGE_STATIC_FILE_DOWNLOAD_EXTENSION = 'zip';
export const FREEBIE_PRODUCT_ID = 0;
export const MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS = window.PMW.MAX_NUMBER_OF_DOWNLOADABLE_PAGES_FOR_PAYG_USERS;
export const DOWNLOAD_MAX_DURATION = 600;
export const DEFAULT_VIDEO_POSTER_GIF_DURATION = 10;
export const DEFAULT_IMAGE_POSTER_GIF_DURATION = 10;

export const MAX_FREEBIE_DIMENSION = 1280;
export const DEFAULT_STATIC_PAGE_DOWNLOAD_DURATION = 5;

export interface PaymentWebhookResponse {
  idOrder: string;
  idOrderKey: string;
  printShopLink?: string;
  thumbnailUrl?: string;
  orderType: 'image' | 'video';
  orderValue: number;
  idPosterHash: string;
  name: string;
  fileType?: string;
  isHeavyOrder?: boolean;
}

export interface PollingResponse {
  isPollingRequired?: string;
  idOrder?: string;
  idOrderKey?: string;
  printShopLink?: string;
  thumbnailUrl?: string;
  orderType?: 'image' | 'video';
  orderValue?: number;
  idPosterHash?: string;
  name: string;
  fileType?: string;
  isHeavyOrder?: boolean;
}

export function getInitialDisplaySizeOption(scale: number | undefined, fileType: FileTypeOptions | undefined, posterObject: PosterLoadObject): SizeDropdownOptions {
  if (!scale || scale === 1) {
    return SizeDropdownOptions.ORIGINAL;
  }

  const {width: pixelWidth, height: pixelHeight} = getWidthAndHeightInPixels(posterObject);
  const maxPixelDimension = Math.max(pixelWidth, pixelHeight);

  const SCALE_EPSILON = 1e-9;
  if (isUserPAYG() && maxPixelDimension > MAX_FREEBIE_DIMENSION && Math.abs(scale - MAX_FREEBIE_DIMENSION / maxPixelDimension) < SCALE_EPSILON) {
    return SizeDropdownOptions.ORIGINAL_COMPRESSED;
  }

  if (fileType === FileTypeOptions.GIF) {
    const minPixelDimension = Math.min(pixelWidth, pixelHeight);
    const smallScale = Math.round(pixelWidth * (540 / minPixelDimension)) / pixelWidth;
    if (scale === smallScale) {
      return SizeDropdownOptions.SMALL;
    }
    const largeScale = Math.round(pixelWidth * (1380 / maxPixelDimension)) / pixelWidth;
    if (scale === largeScale) {
      return SizeDropdownOptions.LARGE;
    }
  } else {
    const {width, height} = getPosterWidthAndHeight(posterObject);
    const minDimension = Math.min(width, height);
    const maxDimension = Math.max(width, height);

    let smallThreshold: number;
    let largeThreshold: number;
    switch (getUnitsFromPosterTypeObject(posterObject)) {
      case PosterTypeUnits.CM:
        smallThreshold = 15;
        largeThreshold = 90;
        break;
      case PosterTypeUnits.FT:
        smallThreshold = 0.5;
        largeThreshold = 4;
        break;
      case PosterTypeUnits.MM:
        smallThreshold = 150;
        largeThreshold = 900;
        break;
      case PosterTypeUnits.INCH:
        smallThreshold = 6;
        largeThreshold = 36;
        break;
      default:
        smallThreshold = 540;
        largeThreshold = 6000;
    }

    const smallScale = Number((width * smallThreshold / minDimension).toFixed(1)) / width;
    if (scale === smallScale) {
      return SizeDropdownOptions.SMALL;
    }
    const largeScale = Number((width * largeThreshold / maxDimension).toFixed(1)) / width;
    if (scale === largeScale) {
      return SizeDropdownOptions.LARGE;
    }
  }

  return SizeDropdownOptions.CUSTOM;
}

export const isPollingRequired = async (paymentIntentId?: string): Promise<PollingResponse> => {
  const response = (await window.PMW.writeLocal('cart/isPollingRequiredForDownloadPayment', {
    paymentIntentId: paymentIntentId,
  })) as PollingResponse;

  return response;
};

export const getChargeIdFromPaymentIntent = async (paymentIntentId?: string): Promise<{chargeId: string}> => {
  const response = (await window.PMW.readLocal('cart/getChargeIdFromPaymentIntent', {
    paymentIntentId: paymentIntentId,
  })) as {chargeId: string};

  if (!response?.chargeId) {
    throw new Error('Failed to get charge ID from payment intent');
  }

  return response;
};

export const checkWebhookStatus = async (paymentData: {chargeId: string}): Promise<PaymentWebhookResponse | null> => {
  const response = (await window.PMW.readLocal('cart/hasSuccessfulDownloadPaymentWebhookProcessed', {
    chargeId: paymentData.chargeId,
  })) as PaymentWebhookResponse | null;

  if (!response?.idOrder) {
    return null;
  }

  return response;
};

export const doesProductRequirePayment = (selectedProductId: number, needsPurchase: boolean): boolean => {
  if (selectedProductId === FREEBIE_PRODUCT_ID) {
    return false;
  }

  return needsPurchase;
};

export enum DownloadPublishMode {
  RAYDIANT = 'raydiant',
  SIGNAGELIVE = 'signagelive',
  SCREENCLOUD = 'screencloud',
  DIGITAL_SIGNAGE = 'digital-signage',
  EMBED_LINKS = 'embed-links',
  DOWNLOAD = 'download',
}

export enum DownloadDialogSource {
  EDITOR = 'editor',
  MYSTUFF = 'mystuff',
  DOWNLOAD_OPTIONS_PAGE = 'downloadOptionsPage',
}

export enum VideoDesignSizeOptionType {
  SD = 'SD',
  HD = 'HD',
  FULL_HD = 'FULL_HD',
  UHD_4K = 'UHD_4K',
  ORIGINAL = 'ORIGINAL',
}

interface FreebiePollingStatus {
  isAvailable: boolean;
  data: {
    status: string;
    downloadUrl: string;
  };
}

interface GenerateDownloadPollingStatus {
  isAvailable: boolean;
  data: {
    status: string;
    downloadUrl: string;
  };
}

interface BatchStatusResponse {
  statuses: Record<
    string,
    | {
        status: string;
        downloadUrl?: string;
        errorMessage?: string;
      }
    | false
  >;
}

export interface PosterProductAPIData {
  isPosterMaker: boolean;
  isExpensiveVideo: boolean;
  idPosterHash: string;
  idGalleryTemplate?: string;
  idTemplate: string;
}

export interface DownloadProductsData {
  id: string;
  name: string;
  description: string;
  rawPrice: number;
  price: string;
  currencyCode: string;
  productPrice: number;
  appleProductId?: string;
  daysFree: number;
  premiumContentAdded: boolean;
  retriesLeft: number;
  creditsLeft: number;
  productCreditPrice: number;
  extraInfo: any[];
  galleryTemplateHash: string | null;
  status: string;
  orderId: number;
  purchaseId: number;
  encryptedPurchaseId: string;
  isWebDownload: boolean;
  orderFileURLs: any[];
  downloadUrl: number;
  downloadFileUrl: number;
  isFree: boolean;
  hashedId: string;
  posterId: string;
  isVideo: boolean;
  remainingCredits: number;
  format: string;
  maxDimension: string;
  height: number;
  width: number;
  hasWatermark: boolean;
  hasClickableLinks: boolean;
  isLargeSize: boolean;
  posterRenderId: string;
  iconClassName: string;
  needsPurchase: boolean;
  posterName: string;
  mimeType: string;
  needsRegenerationDueToDeletion: boolean;
}

export interface Format {
  id: string;
  name: string;
  description: string;
  tag?: string | null;
}

export interface ProductsResponse {
  productsData: DownloadProductsData[];
  upgradeAmount: number;
  upgradeDisplayAmount: string;
  needsPurchase: boolean;
  wasPreviouslyPurchased: boolean;
  isUserPremium?: boolean;
  thumbnails: Record<string, string>;
}

interface ProductsResponseRequiringProcessing {
  thumbnails: string[];
}

export interface DownloadCallbackResponse {
  type: string;
  idOrder: string;
  idOrderKey: string;
  isRealTime: boolean;
  downloadUrl?: string;
  printShopLink?: string;
  thumbnailUrl?: string;
  isHeavyOrder?: boolean;
}

export interface DownloadPaygResponse {
  type: string;
  isRealTime: boolean;
  idOrderKey: string;
  printShopLink?: string;
  idOrder: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  idPosterHash: string;
  isHeavyOrder?: boolean;
}

export interface DownloadFreebieResponse {
  downloadUrl?: string;
  generationKey?: string;
  thumbnailUrl?: string;
}

export interface GenerateDownloadResponse {
  downloadUrl?: string;
  generationKey?: string;
}

export interface BaseDownloadAPIData {
  productId: number;
  isSilent: boolean;
  hashedId: string;
  posterVersionId?: string;
  isFromDownloadOptionsPage?: boolean;
  orderId?: string;
}

export interface CheckoutDownloadAPIData {
  hashedId: string;
  productId: number | string;
  promotion: string;
  mode: string;
  currencyCode: string;
  isInAppPurchase: boolean;
}

interface ImageDownloadOptions {
  bleedValue: number;
  bleedUnits: string;
  scale: number;
  pagesHashedIds?: string[];
}

interface PNGDownloadOptons extends ImageDownloadOptions {
  isTransparent: boolean;
}

interface JPGDownloadOptions extends ImageDownloadOptions {}

interface PDFDownloadOptions extends Omit<ImageDownloadOptions, 'scale'> {
  hasMargin: boolean;
  colorProfile: DownloadColorProfileOptionType;
  letterSize?: LetterSizeOptionType;
  isWebQuality: boolean;
  pagesHashedIds?: string[];
}

export interface DownloadJPGAPIData {
  type: FileTypeOptions.JPG;
  downloadOptions: JPGDownloadOptions;
}

export interface DownloadPNGAPIData {
  type: FileTypeOptions.PNG;
  downloadOptions: PNGDownloadOptons;
}

export interface BaseVideoDownloadOptions {
  speed: number;
  duration: number;
  scale: number;
  pagesHashedIds?: string[];
}

export interface BasePDFAPIData {
  downloadOptions: PDFDownloadOptions;
}

export interface DownloadPDFAPIData extends BasePDFAPIData {
  type: FileTypeOptions.PDF;
}

export interface DownloadMP4APIData {
  type: FileTypeOptions.MP4;
  downloadOptions: BaseVideoDownloadOptions;
}

export interface DownloadGIFAPIData {
  type: FileTypeOptions.GIF;
  downloadOptions: BaseVideoDownloadOptions;
}

// FIGURE OUT WEBP THING AND WEBM THING
export interface DownloadWEBPAPIData {
  type: FileTypeOptions.WEBP;
  downloadOptions: any;
}

export interface DownloadWEBMAPIData {
  type: FileTypeOptions.WEBM;
  downloadOptions: any;
}

export type DownloadPosterAPIData =
  | DownloadJPGAPIData
  | DownloadPNGAPIData
  | DownloadWEBPAPIData
  | DownloadMP4APIData
  | DownloadGIFAPIData
  | DownloadWEBMAPIData
  | DownloadPDFAPIData;

export interface DownloadCheckoutAJAXResponseOrderSuccess {
  type: string;
  isRealTime: boolean;
  idOrderKey: string;
  printShopLink?: string;
  idOrder: string | null;
  thumbnailUrl?: string;
  idPosterHash: string;
  isHeavyOrder?: boolean;
}

interface RealTimeOrderProgressData {
  id: string;
  percentageDone: number;
  sleepTime: number;
}

export interface RealTimeOrderProgressResponse {
  percentageDone: number;
  heaviness: string;
  idProduct: number;
  downloadUrl?: string;
  renderId?: string;
  estimatedTimeInSeconds?: number;
  estimatedTimeStr: string;
  printShopLink?: string;
  error?: string;
}

export type MultipleOrderProgressResponse = Record<string, RealTimeOrderProgressResponse>;

export interface OngoingOrderDetail {
  idOrder: string;
  idOrderKey: string;
  name: string;
  fileType: string | null;
  thumbnailUrl?: string;
}

export interface OngoingOrdersResponse {
  orders: OngoingOrderDetail[];
}

export const getOrderProgress = async (data: RealTimeOrderProgressData): Promise<RealTimeOrderProgressResponse> => {
  return (await window.PMW.readLocal('cart/getRealTimeOrderProgress', {
    id: data.id,
    percentageDone: data.percentageDone,
    sleepTime: data.sleepTime,
  })) as RealTimeOrderProgressResponse;
};

/**
 * Gets progress for multiple orders in a single optimized API call
 * @param orders Array of order objects with id, percentageDone, and sleepTime
 * @returns Object with order IDs as keys and progress data as values
 */
export const getMultipleOrdersProgress = async (orders: RealTimeOrderProgressData[]): Promise<MultipleOrderProgressResponse> => {
  return (await window.PMW.writeLocal('cart/getRealTimeOrderProgressForMultipleOrders', {orders})) as MultipleOrderProgressResponse;
};

export const getOngoingDownloads = async (idPoster?: string): Promise<OngoingOrdersResponse> => {
  return (await window.PMW.readLocal('cart/getOngoingOrdersForUser', {
    idPoster: idPoster,
  })) as OngoingOrdersResponse;
};

export const abortOngoingDownload = async (idOrder: string): Promise<void> => {
  await window.PMW.writeLocal('cart/abortOngoingDownload', {
    idOrder,
  });
};

export const onDownloadPoster = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<DownloadCallbackResponse> => {
  return (await window.PMW.writeLocal('cart/premiumDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    posterVersionId: data?.posterVersionId ?? undefined,
    isFromNewDownloadPage: data?.isFromDownloadOptionsPage ? '1' : '0',
    ...options,
  })) as DownloadCallbackResponse;
};

export const onDownloadPosterWithOptions = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<GenerateDownloadResponse> => {
  return (await window.PMW.writeLocal('cart/generateDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    posterVersionId: data?.posterVersionId ?? undefined,
    isFromNewDownloadPage: data?.isFromDownloadOptionsPage ? '1' : '0',
    orderId: data?.orderId,
    ...options,
  })) as GenerateDownloadResponse;
};

export const onDownloadFreePosterForPaygUser = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<DownloadPaygResponse> => {
  return (await window.PMW.writeLocal('cart/purchaselessPAYGDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    posterVersionId: data?.posterVersionId ?? undefined,
    isFromNewDownloadPage: data?.isFromDownloadOptionsPage ? '1' : '0',
    ...options,
  })) as DownloadPaygResponse;
};

export const onDownloadPosterForFreebie = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<DownloadFreebieResponse> => {
  return (await window.PMW.writeLocal('cart/freebieDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    ...options,
  })) as DownloadFreebieResponse;
};

export const getFreebieDownloadStatus = async (generationKey: string): Promise<FreebiePollingStatus> => {
  return (await window.PMW.readLocal('poster/getFreebieStatus', {
    generationKey: generationKey,
  })) as FreebiePollingStatus;
};

export const getFreebieDownloadStatusForMultipleOrders = async (generationKeys: string[]): Promise<BatchStatusResponse> => {
  return (await window.PMW.readLocal('poster/getFreebieStatusesForMultipleOrders', {
    generationKeys: generationKeys,
  })) as BatchStatusResponse;
};

export const getGenerateDownloadStatus = async (generationKey: string): Promise<GenerateDownloadPollingStatus> => {
  return (await window.PMW.readLocal('poster/getGenerateDownloadStatus', {
    generationKey: generationKey,
  })) as GenerateDownloadPollingStatus;
};

export const getGenerateDownloadStatusForMultipleOrders = async (generationKeys: string[]): Promise<BatchStatusResponse> => {
  return (await window.PMW.readLocal('poster/getGenerateDownloadStatusesForMultipleOrders', {
    generationKeys: generationKeys,
  })) as BatchStatusResponse;
};

export const onDownloadPosterUnpurchased = async (data: CheckoutDownloadAPIData, options: DownloadPosterAPIData): Promise<AJAXResponsePurchaseProduct> => {
  return (await window.PMW.writeLocal('cart/checkoutPublish', {
    idposter: data.hashedId,
    idproduct: data.productId,
    promotion: data.promotion,
    mode: data.mode,
    currencyCode: data.currencyCode,
    isInAppPurchase: data.isInAppPurchase ? 1 : 0,
    ...options,
  })) as AJAXResponsePurchaseProduct;
};

export const getWebDownloadPosterTypeNames = async (): Promise<string[]> => {
  return (await window.PMW.readLocal('postertype/getPosterTypeFromProductId', {
    idProduct: WEB_DOWNLOAD_PRODUCT_ID,
  })) as string[];
};

export const getProductsForPoster = async (posterObj: PosterLoadObject): Promise<ProductsResponse> => {
  const data = (await window.PMW.writeLocal('poster/getDataForDownloadDialog', {
    idPosterHash: posterObj.hashedID,
  })) as Omit<ProductsResponse, 'thumbnails'> & ProductsResponseRequiringProcessing;

  return {
    ...data,
    thumbnails: processThumbnailsFromArrayToHashmap(posterObj.pages.pageOrder, data.thumbnails),
  };
};

export const triggerBrowserDownload = (url: string, posterName: string, extension: string): void => {
  window.PMW.downloadPoster(url, posterName, extension);
};

export const getRecommendedFileTypeFromPosterObject = (posterObject: PosterLoadObject): FileTypeOptions[] => {
  const pageKeys = Object.keys(posterObject.pages.pagesHashMap);
  let isVideo = false;
  let isTransparent = false;
  for (const eachPage of pageKeys) {
    const page = posterObject.pages.pagesHashMap[eachPage];
    if (
      (page.background.details.type === BackgroundTypeName.COLOR && page.background.details.fill.fillType === FillTypes.NONE) ||
      (page.background.details.type === BackgroundTypeName.IMAGE && page.background.details.transparency === transparencyStates.ON)
    ) {
      isTransparent = true;
      break;
    }
  }

  isVideo = isPosterVideo(posterObject);
  const numberOfPages = pageKeys.length;

  if (isVideo) {
    return [FileTypeOptions.MP4];
  }

  if (isTransparent) {
    if (numberOfPages > 1) {
      return [FileTypeOptions.PNG, FileTypeOptions.PDF];
    }
    return [FileTypeOptions.PNG];
  }

  if (numberOfPages > 1) {
    return [FileTypeOptions.JPG, FileTypeOptions.PDF];
  }
  return [FileTypeOptions.JPG];
};

export const isDurationEnabledForGif = (posterObject: PosterLoadObject): boolean => {
  return isPosterVideo(posterObject);
};

export const getDurationForPoster = (poster: PosterLoadObject): number => {
  const pageKeys = Object.keys(poster.pages.pagesHashMap);
  if (isPosterVideo(poster)) {
    let duration = 0;
    for (const eachPage of pageKeys) {
      const page = poster.pages.pagesHashMap[eachPage];
      duration += page.duration;
    }
    return duration;
  } else {
    return DEFAULT_STATIC_PAGE_DOWNLOAD_DURATION * pageKeys.length;
  }
};

export const getDurationForSelectedPages = (poster: PosterLoadObject, selectedPageIds: string[]): number => {
  if (isPosterVideo(poster)) {
    let duration = 0;
    for (const pageId of selectedPageIds) {
      const page = poster.pages.pagesHashMap[pageId];
      if (page) {
        duration += page.duration;
      }
    }
    return duration;
  }
  return 5 * selectedPageIds.length;
};

const UPSELLING_HAS_SEEN_COOKIE = 'pmwduhs';
const UPSELLING_AI_CHAT_FIRST_TIME_SEEN_COOKIE = 'pmwduhs_ai_chat';

export const shouldShowDownloadUpsellingForUser = (): boolean => {
  const value = readCookie(UPSELLING_HAS_SEEN_COOKIE);

  if (!value) {
    return true;
  }

  const hasSeenValue = Number(value);
  return hasSeenValue !== 1;
};

export const shouldShowFirstTimeAiChatUpselling = (): boolean => {
  return !readCookie(UPSELLING_AI_CHAT_FIRST_TIME_SEEN_COOKIE);
};

const UPSELLING_AI_CHAT_FIRST_TIME_COOKIE_DAYS = 365;

export const setFirstTimeAiChatUpsellingSeenCookie = (): void => {
  setCookie(UPSELLING_AI_CHAT_FIRST_TIME_SEEN_COOKIE, '1', UPSELLING_AI_CHAT_FIRST_TIME_COOKIE_DAYS);
};

export const getProductIdForVideoDownloadsForNonPremiumUsers = (
  videoScale: VideoDesignSizeOptionType,
  duration: number,
  selectedPages: number,
  posterObject: PosterLoadObject
): number => {
  if (selectedPages > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
    return EXPENSIVE_PRODUCT_ID;
  }

  if (duration > 20 || videoScale === VideoDesignSizeOptionType.FULL_HD || videoScale === VideoDesignSizeOptionType.UHD_4K) {
    if (!isExpensiveVideoPoster(posterObject)) {
      return CHEAP_PRODUCT_ID;
    }
    return EXPENSIVE_PRODUCT_ID;
  }
  return FREEBIE_PRODUCT_ID;
};

export const getProductIdForGifDownloadsForNonPremiumUsers = (
  duration: number,
  scale: number,
  posterObject: PosterLoadObject,
  isWebDownload: boolean,
  selectedPages: number
): number => {
  if (selectedPages > MAX_PAGES_ALLOWED_FOR_CHEAP_PRODUCT) {
    return EXPENSIVE_PRODUCT_ID;
  }

  if (duration > 10 || isScaleAboveTheMaxFreebieDimension(scale, posterObject, isWebDownload)) {
    return CHEAP_PRODUCT_ID;
  }
  return FREEBIE_PRODUCT_ID;
};

export const isScaleAboveTheMaxFreebieDimension = (scale: number, posterObject: PosterLoadObject, isWebDownload: boolean): boolean => {
  if (isWebDownload) {
    return scale > 1;
  }
  const {width, height} = getWidthAndHeightInPixels(posterObject);
  return Math.max(width * scale, height * scale) > MAX_FREEBIE_DIMENSION;
};

export const setDownloadUpsellingCookie = (): void => {
  setCookie(UPSELLING_HAS_SEEN_COOKIE, '1', isUserPremium() ? 7 : 1);
};

export const openCheckoutDialogForTrialPremiumUsers = (status: string, successCallback: VoidFunction, errorCallback?: VoidFunction, closeCallback?: VoidFunction): void => {
  window.PMW.premiumDialogs.openConvertTrialDialog(
    status,
    () => {
      successCallback();
      return;
    },
    (): void => {
      onConvertTrialFailCallback(status);
      if (errorCallback) {
        errorCallback();
      }
      return;
    },
    (): void => {
      if (closeCallback) {
        closeCallback();
      }
      return;
    }
  );
};

export const POLLING_INTERVAL = 3000;
export const MAX_POLLING_ATTEMPTS = 200;
export const DOWNLOAD_DELAY_MS = 1000;

export interface OrderFilterOptions {
  excludeDownloadPageOrder?: boolean;
  excludeFreebieDownloadPageOrder?: boolean;
  excludePaygDownload?: boolean;
  includeOnlyPaygDownload?: boolean;
  includeOnlyDownloadPageOrder?: boolean;
  includeOnlyFreebieDownloadPageOrder?: boolean;
  includeDownloadPageOrFreebie?: boolean;
}

export interface OrderStateUpdateData {
  orderIdKey: string;
  orderId: string;
  name: string;
  fileType: string;
  thumbnailUrl?: string;
  isDropboxDownload?: boolean;
  isGoogleDriveDownload?: boolean;
  isPaygDownload?: boolean;
  isDownloadPageOrder?: boolean;
  isFreebieDownloadPageOrder?: boolean;
  generationKey?: string;
  downloadResult?: DownloadCallbackResponse;
}

export const filterOrdersByOptions = <
  T extends {orderIdKey: string; state: string; isDownloadPageOrder?: boolean; isFreebieDownloadPageOrder?: boolean; isPaygDownload?: boolean; generationKey?: string},
>(
  orders: T[],
  options: OrderFilterOptions,
  stillDownloadingSet?: Set<string>
): T[] => {
  return orders.filter((order): order is T => {
    if (!order) return false;

    if (options.excludeDownloadPageOrder && order.isDownloadPageOrder === true) return false;
    if (options.excludeFreebieDownloadPageOrder && order.isFreebieDownloadPageOrder === true) return false;
    if (options.excludePaygDownload && order.isPaygDownload === true) return false;

    if (options.includeOnlyPaygDownload && order.isPaygDownload !== true) return false;
    if (options.includeOnlyDownloadPageOrder && order.isDownloadPageOrder !== true) return false;
    if (options.includeOnlyFreebieDownloadPageOrder && order.isFreebieDownloadPageOrder !== true) return false;

    if (options.includeDownloadPageOrFreebie) {
      const isManaged = order.isDownloadPageOrder === true || order.isFreebieDownloadPageOrder === true;
      if (!isManaged) return false;
    }

    const isDownloading = order.state === 'downloading' || (stillDownloadingSet?.has(order.orderIdKey) ?? false);

    if (options.includeDownloadPageOrFreebie && isDownloading && !order.generationKey) {
      return false;
    }

    return isDownloading;
  });
};

export const createBaseStateUpdate = (order: OrderStateUpdateData): Omit<Partial<OrderStateUpdateData>, 'state'> => {
  return {
    orderIdKey: order.orderIdKey,
    orderId: order.orderId,
    name: order.name,
    fileType: order.fileType,
    thumbnailUrl: order.thumbnailUrl,
    isDropboxDownload: order.isDropboxDownload,
    isGoogleDriveDownload: order.isGoogleDriveDownload,
    isPaygDownload: order.isPaygDownload,
    isDownloadPageOrder: order.isDownloadPageOrder,
    isFreebieDownloadPageOrder: order.isFreebieDownloadPageOrder,
    generationKey: order.generationKey,
    downloadResult: order.downloadResult,
  };
};

export const createReadyStateUpdate = (
  order: OrderStateUpdateData,
  downloadURL: string,
  printShopLink?: string
): Omit<Partial<OrderStateUpdateData>, 'state'> & {downloadURL: string; downloadResult?: DownloadCallbackResponse} => {
  const normalizedPrintShopLink = printShopLink ?? '';
  return {
    ...createBaseStateUpdate(order),
    downloadURL,
    downloadResult:
      order.downloadResult === undefined
        ? {
            type: 'success',
            idOrder: order.orderId,
            idOrderKey: order.orderIdKey,
            isRealTime: true,
            downloadUrl: downloadURL,
            printShopLink: normalizedPrintShopLink,
            thumbnailUrl: order.thumbnailUrl,
          }
        : {
            ...order.downloadResult,
            printShopLink: normalizedPrintShopLink,
          },
  };
};

export const createErrorStateUpdate = (order: OrderStateUpdateData): Omit<Partial<OrderStateUpdateData>, 'state'> => {
  return createBaseStateUpdate(order);
};

export const createDownloadingStateUpdate = (order: OrderStateUpdateData, downloadProgress: number): Omit<Partial<OrderStateUpdateData>, 'state'> & {downloadProgress: number} => {
  return {
    ...createBaseStateUpdate(order),
    downloadProgress,
  };
};

export const triggerDownloadWithDelay = (downloadUrl: string, posterName: string, fileType: string, downloadIndex: number, delayMs: number = DOWNLOAD_DELAY_MS): void => {
  setTimeout(() => {
    triggerBrowserDownload(downloadUrl, posterName, fileType);
  }, downloadIndex * delayMs);
};

export const IAP_PRODUCT_TYPE_DOWNLOADS = 'downloads';

export interface AppleDownloadProduct {
  appleProductId: string;
  price: number;
  displayPrice: string;
  currencyCode: string;
}

export type AppleDownloadProductsMap = Record<string, AppleDownloadProduct>;

export interface AppleSubscriptionUpgradePrice {
  upgradeAmount: number;
  upgradeDisplayAmount: string;
}

const PREMIUM_SUBSCRIPTION_LEVEL = 1;
const MONTHLY_BILLING_DURATION = 1;
const SINGLE_SEAT_TIER = 1;

export const fetchAppleSubscriptionUpgradePrice = async (): Promise<AppleSubscriptionUpgradePrice | undefined> => {
  let result: AppleSubscriptionUpgradePrice | undefined;

  await (window.PMW.getIAPProducts(window.PMW.IAP_PRODUCT_TYPES.SUBSCRIPTIONS, async (products: IAPProduct[]) => {
    const monthlyPremiumProduct = products.find(
      (product) => product.level === PREMIUM_SUBSCRIPTION_LEVEL && product.duration === MONTHLY_BILLING_DURATION && product.tierId === SINGLE_SEAT_TIER
    );

    if (monthlyPremiumProduct) {
      result = {
        upgradeAmount: monthlyPremiumProduct.price,
        upgradeDisplayAmount: monthlyPremiumProduct.displayPrice,
      };
    }
  }) as unknown as Promise<void>);

  return result;
};

export const fetchAppleDownloadProducts = async (productsData: DownloadProductsData[]): Promise<AppleDownloadProductsMap> => {
  const appleProductsMap: AppleDownloadProductsMap = {};

  await (window.PMW.getIAPProducts(IAP_PRODUCT_TYPE_DOWNLOADS, async (appleProducts: IAPProduct[]) => {
    const iapMap = appleProducts.reduce<Record<string, IAPProduct>>((map, product) => {
      map[product.id] = product;
      return map;
    }, {});

    productsData.forEach((product) => {
      if (product.appleProductId && iapMap[product.appleProductId]) {
        const appleProduct = iapMap[product.appleProductId];
        const appleDownloadProduct: AppleDownloadProduct = {
          appleProductId: product.appleProductId,
          price: appleProduct.price,
          displayPrice: appleProduct.displayPrice,
          currencyCode: appleProduct.currencyCode,
        };

        appleProductsMap[product.id] = appleDownloadProduct;
        appleProductsMap[product.appleProductId] = appleDownloadProduct;
      }
    });
  }) as unknown as Promise<void>);

  return appleProductsMap;
};

export const getDisplayPriceForProduct = (product: DownloadProductsData | undefined, appleDownloadProducts?: AppleDownloadProductsMap): string | undefined => {
  if (!product) {
    return undefined;
  }

  if (isIOSApp() && appleDownloadProducts?.[product.id]) {
    return appleDownloadProducts[product.id].displayPrice;
  }

  return product.price;
};

export const getCurrencyCodeForProduct = (product: DownloadProductsData | undefined, appleDownloadProducts?: AppleDownloadProductsMap): string => {
  if (!product) {
    return DEFAULT_DOWNLOAD_CURRENCY;
  }

  if (isIOSApp() && appleDownloadProducts?.[product.id]) {
    return appleDownloadProducts[product.id].currencyCode;
  }

  return product.currencyCode;
};

export const getProductPriceForProduct = (product?: DownloadProductsData, appleDownloadProducts?: AppleDownloadProductsMap): number => {
  if (!product) {
    return 0;
  }

  if (isIOSApp() && appleDownloadProducts?.[product.id]) {
    return appleDownloadProducts[product.id].price;
  }

  return product.productPrice;
};

export const processIAPDownloadPurchase = async (
  checkoutId: string,
  appleProductId: string,
  onSuccess: (orderInfo: DownloadCheckoutAJAXResponseOrderSuccess) => void,
  onError: (showError?: boolean) => void
): Promise<void> => {
  try {
    window.PMW.callBackForIAPurchase(
      appleProductId,
      checkoutId,
      IAP_PRODUCT_TYPE_DOWNLOADS,
      async (transaction: {idTransaction: string}) => {
        try {
          const orderInfo = (await window.PMW.pollingAjaxCallAsync(
            async (): Promise<any> => {},
            () => {
              return window.PMW.readLocal('cart/hasSuccessfulIAPDownloadTransactionProcessed', {idTransaction: transaction.idTransaction});
            }
          )) as DownloadCheckoutAJAXResponseOrderSuccess;

          onSuccess(orderInfo);
        } catch {
          onError();
        }
      },
      (showError: boolean) => {
        onError(showError);
      }
    );
  } catch {
    onError();
  }
};
